// src/PhotoGallery.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import GalleryHeader from "../components/Gallery/GalleryHeader";
import GalleryControls from "../components/Gallery/GalleryControls";
import PhotoGrid from "../components/Gallery/PhotoGrid";
import PhotoList from "../components/Gallery/PhotoList";
import PhotoModal from "../components/Gallery/PhotoModal";
import UploadModal from "../components/Gallery/UploadModal";
import Loader from "../components/Loader";
import { fetcheventsRequest } from "../store/slice/events/events.slice";
import { fetchPhotosRequest } from "../store/slice/images/images.slice";
import { getEventIdFromPath } from "../utils/getEventId";
import DynamicModal from "../components/Generic/DynamicModal";
import EventLinkGenerator from "../components/Gallery/EventLinkGenerator";
import InfiniteScroll from "react-infinite-scroll-component";

const PhotoGallery = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [viewMode, setViewMode] = useState("grid");
  const [searchTerm, setSearchTerm] = useState("");
  const [displayedPhotos, setDisplayedPhotos] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState("Event Photos");
  const [showShareModal, setShowShareMoodal] = useState(false);
  const [currentCount, setCurrentCount] = useState(0);
  const [category, setCategory] = useState("all");

  const { photos: allPhotos } = useSelector((state) => state.photos);
  const { events } = useSelector((state) => state.events);
  const eventId = getEventIdFromPath(location.pathname);

  useEffect(() => {
    if (eventId) {
      dispatch(fetcheventsRequest());
      dispatch(fetchPhotosRequest(eventId));
    }
  }, [eventId]);

  const filteredPhotos =
    allPhotos?.filter((photo) =>
      photo.title.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

  useEffect(() => {
    if (!allPhotos || allPhotos.length === 0) return;

   const filteredPhotos =
  category === "all"
    ? allPhotos
    : allPhotos.filter((photo) => photo.category === category);

// Remove duplicates based on photo.id
const uniquePhotos = Array.from(
  new Map(filteredPhotos.map((photo) => [photo.id, photo])).values()
);

    // ✅ Only set first 20 if displayedPhotos is empty (initial load)
    setDisplayedPhotos((prev) => {
        return uniquePhotos.slice(0, 20);
    });
    setCurrentCount(20);

    setHasMore(uniquePhotos.length > displayedPhotos.length);
    setLoading(false);
  }, [allPhotos,category]); // ✅ keep only allPhotos dependency

  const loadMorePhotos = () => {
    setLoading(false);
    const currentLength = currentCount;
    setCurrentCount(currentLength + 20);

    const uniquePhotos = Array.from(
      new Map(allPhotos.map((photo) => [photo.id, photo])).values()
    );
    const nextPhotos = uniquePhotos.slice(currentLength, currentLength + 20);
    setDisplayedPhotos((prev) => [...prev, ...nextPhotos]);
    setHasMore(currentLength + nextPhotos.length < uniquePhotos.length);
  };

  useEffect(() => {
    if (events?.length > 0) {
      const eventName =
        events.find((e) => e.id == eventId)?.name || "Event Photos";
      setSelectedEvent(eventName);
    }
  }, [events, eventId]);
  return (
    <div className="min-h-screen bg-gray-50">
      {loading ? (
        <div className="flex items-center justify-center h-screen">
          <Loader size="large" fullscreen />
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <GalleryHeader
            eventName={selectedEvent}
            photoCount={filteredPhotos.length}
            onUploadClick={() => setShowUploadModal(true)}
            onShare={() => setShowShareMoodal(true)}
          />

          <GalleryControls
            viewMode={viewMode}
            setViewMode={setViewMode}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            setCategory={setCategory}
            category={category}
          />

          {viewMode === "grid" ? (
            <PhotoGrid
              displayedPhotos={displayedPhotos}
              allPhotos={allPhotos}
              loadMorePhotos={loadMorePhotos}
              onPhotoClick={setSelectedPhoto}
              setLoading={setLoading}
              onUploadClick={() => setShowUploadModal(true)}
              hasMore={hasMore}
            />
          ) : (
            <PhotoList
              photos={filteredPhotos}
              onPhotoClick={setSelectedPhoto}
            />
          )}

          <PhotoModal
            photo={selectedPhoto}
            photos={filteredPhotos} // pass the full photo list
            currentIndex={filteredPhotos?.findIndex(
              (p) => p.id === selectedPhoto?.id
            )} // current photo index
            onClose={() => setSelectedPhoto(null)}
            onPrev={() => {
              const currentIndex = filteredPhotos?.findIndex(
                (p) => p.id === selectedPhoto?.id
              );
              if (currentIndex > 0)
                setSelectedPhoto(filteredPhotos[currentIndex - 1]);
            }}
            onNext={() => {
              const currentIndex = filteredPhotos?.findIndex(
                (p) => p.id === selectedPhoto?.id
              );
              if (currentIndex < filteredPhotos?.length - 1)
                setSelectedPhoto(filteredPhotos[currentIndex + 1]);
            }}
          />

          <UploadModal
            show={showUploadModal}
            onClose={() => setShowUploadModal(false)}
            eventName={eventId}
            eventRealName={selectedEvent}
          />
          {showShareModal && (
            <DynamicModal
              showModal={showShareModal}
              handleClose={() => setShowShareMoodal(false)}
              title={`Share Photos URL ${selectedEvent}`}
              body={
                <EventLinkGenerator
                  eventId={eventId}
                  pin={events?.filter((e) => e.id == eventId)?.[0]?.pass_key}
                />
              }
              size="lg"
              showButtons={false}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default PhotoGallery;
